const BASE_WIDTH = 620
const BASE_HEIGHT = 436
const STAR_PATH = 'polygon(50% 0%, 58% 42%, 100% 50%, 58% 58%, 50% 100%, 42% 58%, 0% 50%, 42% 42%)'

interface AnimatedLogoProps {
  /** Rendered height in px; all proportions scale from this. */
  size?: number
  className?: string
}

export default function AnimatedLogo({ size = 44, className }: AnimatedLogoProps) {
  const scale = size / BASE_HEIGHT
  const width = BASE_WIDTH * scale
  const px = (value: number) => value * scale

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width,
        height: size,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden="true"
    >
      {/* offset base card, animates through accent colors */}
      <div
        style={{
          position: 'absolute',
          inset: `${px(52)}px 0 0 ${px(52)}px`,
          overflow: 'hidden',
          clipPath: `inset(0 round ${px(74)}px)`,
          transform: `translate(${px(34)}px, ${px(29)}px) rotate(-5deg)`,
          borderRadius: px(74),
          background: 'rgba(255,255,255,0.04)',
          border: `${Math.max(px(2), 1)}px solid rgba(255,255,255,0.34)`,
          boxShadow: `0 ${px(54)}px ${px(120)}px rgba(0,0,0,0.5), inset 0 ${px(2)}px 0 rgba(255,255,255,0.5)`,
        }}
      >
        <div
          style={{ position: 'absolute', inset: 0, animation: 'm2bLogoCardBase 13.8s linear infinite' }}
        />
        <div
          style={{
            position: 'absolute',
            top: '-100%',
            left: 0,
            width: '27%',
            height: '300%',
            animation: 'm2bLogoSweep 4.6s linear infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: '50%',
              width: px(3000),
              animation: 'm2bLogoCardSlab 13.8s linear infinite',
            }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            top: '-100%',
            left: 0,
            width: '27%',
            height: '300%',
            background:
              'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0) 100%)',
            filter: `blur(${px(12)}px)`,
            mixBlendMode: 'screen',
            animation: 'm2bLogoSweep 4.6s linear infinite',
          }}
        />
      </div>

      {/* front glass card */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          clipPath: `inset(0 round ${px(74)}px)`,
          transform: 'rotate(2deg)',
          borderRadius: px(74),
          background:
            'linear-gradient(158deg, rgba(64,72,90,0.72) 0%, rgba(22,27,38,0.78) 40%, rgba(10,13,20,0.82) 100%)',
          border: `${Math.max(px(2), 1)}px solid rgba(255,255,255,0.42)`,
          boxShadow: `inset 0 ${px(2)}px 0 rgba(255,255,255,0.55)`,
          backdropFilter: `blur(${px(26)}px) saturate(1.3)`,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-100%',
            left: 0,
            width: '27%',
            height: '300%',
            background:
              'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.68) 50%, rgba(255,255,255,0) 100%)',
            filter: `blur(${px(9)}px)`,
            mixBlendMode: 'screen',
            animation: 'm2bLogoSweep 4.6s linear infinite',
          }}
        />
      </div>

      {/* wordmark */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: px(5),
          fontFamily: 'var(--font-display)',
          lineHeight: 0.9,
          color: '#fff',
          letterSpacing: '-0.02em',
          textShadow: `0 ${px(4)}px ${px(34)}px rgba(0,0,0,0.4)`,
        }}
      >
        <span style={{ fontSize: px(260) }}>M</span>
        <span
          style={{
            fontSize: px(137),
            marginTop: px(29),
            animation: 'm2bLogoTwo 13.8s linear infinite',
          }}
        >
          2
        </span>
        <span style={{ fontSize: px(260) }}>B</span>
      </div>

      {/* stars */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          clipPath: `inset(0 round ${px(74)}px)`,
          transform: 'rotate(2deg)',
          borderRadius: px(74),
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: px(28),
            right: px(34),
            width: px(96),
            height: px(96),
            background: '#fff',
            clipPath: STAR_PATH,
            filter: `drop-shadow(0 0 ${px(24)}px rgba(255,255,255,0.85))`,
            animation: 'm2bLogoStar 4.6s ease-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: px(34),
            right: px(118),
            width: px(48),
            height: px(48),
            background: 'var(--flagship-yellow)',
            clipPath: STAR_PATH,
            filter: `drop-shadow(0 0 ${px(18)}px rgba(255,221,51,0.8))`,
            animation: 'm2bLogoStarSmall 4.6s ease-out infinite',
          }}
        />
      </div>
    </div>
  )
}
